// Token Imports
import { REFRESH_COOKIE_HINT } from './session-cookies'
import { clearAccessToken, getAccessToken, setAccessToken } from './token-store'

export const API_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3011/api'

export class ApiError extends Error {
  readonly statusCode: number
  readonly errors: string[]

  /**
   * АЛДААНЫ БҮТЭН JSON БИЕ (Ү5, docs/31 §4o) — `message`-ээс гадуурх талбар (`code`, `current`, `reason`)
   * хэрэгтэй дуудагчид: 409-ийн серверийн одоогийн мөр (`BiPersonalBoardConflict.current`) г.м.
   * JSON биш бие (прокси HTML) бол `undefined`. Өмнөх дуудагчид хөндөгдөхгүй (нэмэлт талбар).
   */
  readonly body: unknown

  constructor(statusCode: number, message: string, errors: string[] = [], body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.errors = errors
    this.body = body
  }
}

type FetchOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
}

type RequestOptions = FetchOptions & {

  /** Set for the refresh call itself, so a failed refresh cannot recurse. */
  skipRefresh?: boolean

  /**
   * ХОЁРТЫН ХАРИУ — `.xlsx`, `.pdf` гэх мэт. `Blob` буцаана, JSON задлахгүй.
   *
   * ! 🔴 ЯАГААД ИЛ СОНГОЛТ БОЛСОН БЭ: урьд нь дуудагчид `apiFetch<Blob>(…)`
   * ! гэж бичдэг байсан ч ТЭР ТӨРӨЛ ХУДАЛ байв — `parseSuccess` нь ҮРГЭЛЖ
   * ! `JSON.parse` хийдэг тул хариу нь .xlsx байсан ч «Серверийн хариу JSON
   * ! биш байна» гэж шиддэг. Өөрөөр хэлбэл платформын БҮХ Excel экспорт
   * ! ажиллахгүй байсан (production дээр 2026-09-20-нд `/timesheet/monthly`
   * ! ба `/erp/1c-detail` хоёуланд нь баталсан: сүлжээ 200 + зөв MIME
   * ! буцаасан атал toast «Татаж чадсангүй» гэж гарч байв).
   * !
   * ! Content-type-аар АВТОМАТААР таах шийдлийг СОНГООГҮЙ: прокси HTML
   * ! алдааны хуудас буцаахад (502/504) түүнийг чимээгүй blob болгож,
   * ! хэрэглэгч эвдэрсэн файл татах байв. Ил зарласан үед л хоёртоор уншина.
   */
  binary?: boolean
}

/** In-flight refresh, shared so a burst of 401s triggers one round trip, not N. */
let refreshInFlight: Promise<boolean> | null = null

/**
 * Whether it is worth asking the API to restore a session.
 *
 * The refresh token is httpOnly, so JS cannot see it; the API sets a readable
 * companion cookie beside it that carries no secret and proves nothing. Without
 * this check every signed-out visitor spent a round trip on `/auth/refresh` and
 * logged a 401 in the console on each page load.
 *
 * A stale hint costs one request — the API clears it and answers 401, exactly as
 * before. Missing it on the server side would be the real bug, so the server
 * never trusts it.
 */
const hasSessionHint = (): boolean => {
  if (typeof document === 'undefined') return false

  return document.cookie.split('; ').some(entry => entry.startsWith(`${REFRESH_COOKIE_HINT}=`))
}

const refreshSession = async (): Promise<boolean> => {
  if (!hasSessionHint()) {
    clearAccessToken()

    return false
  }

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      })

      if (!response.ok) {
        clearAccessToken()

        return false
      }

      const payload = (await response.json()) as { accessToken: string }

      setAccessToken(payload.accessToken)

      return true
    } catch {
      clearAccessToken()

      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/*
 * Accepts both forms callers use: a resource path (`/users`) and an absolute URL,
 * which is what Refine's `useCustom` passes after `dataProvider.getApiUrl()`.
 * Without the absolute case the base would be prefixed twice.
 */
const buildUrl = (path: string, query?: RequestOptions['query']): string => {
  const isAbsolute = /^https?:\/\//i.test(path)
  const url = new URL(isAbsolute ? path : `${API_URL}${path.startsWith('/') ? path : `/${path}`}`)

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  return url.toString()
}

const toApiError = async (response: Response): Promise<ApiError> => {
  let message = response.statusText || 'Хүсэлт амжилтгүй боллоо'
  let errors: string[] = []
  let parsed: unknown

  try {
    const body = (await response.json()) as { message?: string | string[] }

    parsed = body

    if (Array.isArray(body.message)) {
      errors = body.message
      message = body.message[0] ?? message
    } else if (body.message) {
      message = body.message
    }
  } catch {
    // Non-JSON error body (proxy timeout, HTML error page): keep the status text.
  }

  return new ApiError(response.status, message, errors, parsed)
}

const send = async (path: string, options: FetchOptions): Promise<Response> => {
  const { body, query, headers, ...rest } = options
  const token = getAccessToken()

  /*
   * ! `FormData` (multipart) ТЭР ЧИГЭЭРЭЭ. `Content-Type`-ыг хөтөч өөрөө
   * ! `boundary`-тай нь тавина — гараар `application/json` эсвэл boundary-гүй
   * ! `multipart/form-data` тавибал сервер файлыг уншиж чадахгүй.
   * ! Хэрэглээ: вэбсайтын видео (`website/upload-media.ts`) — 23 МБ mp4-ийг
   * ! base64 JSON-оор илгээвэл серверийн 20 МБ хязгаарт 413 авна.
   */
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData

  return fetch(buildUrl(path, query), {
    ...rest,
    credentials: 'include',
    headers: {
      ...(body === undefined || multipart ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers
    },
    ...(body === undefined ? {} : { body: multipart ? body : JSON.stringify(body) })
  })
}

/** Биегүй байхаар ТӨЛӨВЛӨГДСӨН статусууд — эдгээрт хоосон бие нь ХЭВИЙН. */
const EMPTY_BY_DESIGN = new Set([204, 205, 304])

/**
 * АМЖИЛТТАЙ ХАРИУГ УНШИНА — `.json()`-ыг ШУУД дуудахгүй.
 *
 * ! 🔴 `await response.json()` нь ХООСОН бие дээр
 * ! `Failed to execute 'json' on 'Response': Unexpected end of JSON input`
 * ! гэж унадаг. Тэр мессеж нь ЮУ болсныг огт хэлдэггүй тул хэрэглэгч ч,
 * ! бид ч шалтгааныг олохгүй — production-д «их олон газар» гарч байв
 * ! (2026-09-12).
 *
 * ! ХООСОН БИЕ ХААНААС ГАРДАГ ВЭ:
 * !   · 204/205/304 — ЗӨВ, биегүй байх ёстой
 * !   · 200 боловч холболт ТАСАРСАН — nginx-ийн `proxy_read_timeout`
 * !     (анхдагч 60с) дууссан, эсвэл upstream унасан. `/warehouse/orders`
 * !     нь 154 секунд болдог тул яг үүнд өртдөг.
 * !
 * ! 🔴 ХООСОН 200-ЫГ `undefined` ГЭЖ ЗАЛГИХГҮЙ. Тэгвэл дэлгэц «0 захиалга»
 * ! гэж БУРУУ өгөгдөл харуулна — тасарсан хариуг «хоосон дүн» болгон
 * ! хувиргах нь чимээгүй унахаас ДОР. Тодорхой алдаа шидэж, шалтгааныг
 * ! хэлнэ.
 */
const parseSuccess = async <T>(response: Response): Promise<T> => {
  if (EMPTY_BY_DESIGN.has(response.status)) return undefined as T

  const text = await response.text()

  if (text.trim() === '') {
    throw new ApiError(response.status, 'Сервер хоосон хариу буцаав — хүсэлт хугацаа хэтэрч тасарсан байж магадгүй', [])
  }

  try {
    return JSON.parse(text) as T
  } catch {
    /*
     * ! JSON БИШ БИЕ. Прокси HTML буцаасан (504, 502), эсвэл хариу
     * ! ТАЙРАГДСАН. Түүхий HTML-ийг хэрэглэгчид харуулахгүй — эхний
     * ! 120 тэмдэгтийг л оруулна.
     */
    throw new ApiError(response.status, 'Серверийн хариу JSON биш байна', [text.slice(0, 120)])
  }
}

/**
 * One request, with a single transparent retry after refreshing an expired
 * access token. Anything other than a 401 — including a 403 from the permission
 * guard — surfaces as an ApiError for the caller to render.
 */
export const apiFetch = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const { skipRefresh = false, binary = false, ...request } = options

  let response = await send(path, request)

  if (response.status === 401 && !skipRefresh && (await refreshSession())) {
    response = await send(path, request)
  }

  if (!response.ok) {
    throw await toApiError(response)
  }

  /*
   * ! ХОЁРТЫН ХАРИУГ JSON ГЭЖ ЗАДЛАХГҮЙ. Алдааны замыг (`!response.ok`)
   * ! ЭНЭ САЛААНААС ӨМНӨ шалгасан тул 4xx/5xx нь хэвийнхээрээ `ApiError`
   * ! болно — зөвхөн АМЖИЛТТАЙ хоёртын бие энд хүрнэ.
   */
  if (binary) return (await response.blob()) as T

  return parseSuccess<T>(response)
}

export { refreshSession }
